/** Strings of the session screens; merged into `ca` by src/i18n/ca.ts. */
export const ca_session = {
  /** «Com funciona aquesta nit» (MECHANICS.md R18, step D1.3): the briefing's first card. */
  howItWorks: {
    title: 'Com funciona aquesta nit',
    hint: 'El mateix que llegireu a dalt de tot del briefing del joc, abans de l’ordre.',
  },
  /**
   * The timeline's sentences (the owner's review of 2026-10-12): every event
   * the runtime sends, as a person reads it. Never a class, never an event's id,
   * and a sentence, not a label and a colon (the owner, test card 172,
   * 2026-10-17: «Baixa enemiga: Rifleman» is not how anybody tells a night).
   */
  sessionTimeline: {
    /** Whoever the catalogue has no name for. */
    soldier: 'soldat',
    /**
     * One casualty. `who` is the catalogue's name of the man (a soldier of
     * either side, a civilian) or, with `player`, the name a player played
     * under; empty when the reader may not see it.
     */
    casualty: {
      enemy: (who: string) => `Ha caigut un ${who || 'soldat'} enemic`,
      own: (who: string, player?: boolean) =>
        !who
          ? 'Ha caigut un dels nostres'
          : player
            ? `Ha caigut ${who}, dels nostres`
            : `Ha caigut un ${who} dels nostres`,
      civilian: (who: string) => (who ? `Ha mort un civil (${who})` : 'Ha mort un civil'),
    } as Record<string, (who: string, player?: boolean) => string>,
    casualties: {
      enemy: (n: number) => `Han caigut ${n} enemics`,
      own: (n: number) => `Han caigut ${n} dels nostres`,
      civilian: (n: number) => `Han mort ${n} civils`,
    } as Record<string, (n: number) => string>,
    /** An objective's new state, as a sentence about it. */
    objective: {
      pending: (title: string) => `L’objectiu «${title}» queda pendent`,
      active: (title: string) => `Comença l’objectiu «${title}»`,
      done: (title: string) => `S’ha assolit l’objectiu «${title}»`,
      failed: (title: string) => `S’ha fallat l’objectiu «${title}»`,
      skipped: (title: string) => `L’objectiu «${title}» queda de banda`,
      cancelled: (title: string) => `S’ha cancel·lat l’objectiu «${title}»`,
    } as Record<string, (title: string) => string>,
    objectiveOther: (title: string, state: string) => `L’objectiu «${title}» ara és ${state}`,
    objectiveAdded: (title: string) =>
      title ? `El comandament afegeix l’objectiu «${title}»` : 'El comandament afegeix un objectiu',
    supportGranted: (n: number, name: string) =>
      n === 1 ? `El comandament concedeix ${name}` : `El comandament concedeix ${name} (${n})`,
    supportReady: (name: string) => `${name} torna a estar disponible`,
    /** What a sender said on the radio, quoted: the sender in its own words. */
    radio: (from: string, text: string) =>
      from ? `${from}, per ràdio: «${text}»` : `Per ràdio: «${text}»`,
    voteClosed: (question: string, result: string | null) =>
      result
        ? `La votació «${question}» s’ha tancat amb «${result}»`
        : `La votació «${question}» s’ha tancat sense resultat`,
    intel: (title: string) => (title ? `S’ha descobert «${title}»` : 'S’ha descobert informació'),
    marker: (text: string) =>
      text ? `Apareix una marca nova al mapa, «${text}»` : 'Apareix una marca nova al mapa',
    twist: (title: string) => (title ? `La nit fa un gir, «${title}»` : 'La nit fa un gir'),
    ended: {
      success: 'S’ha complert la missió',
      partial: 'La missió s’ha complert en part',
      failed: 'La missió ha fallat',
      failure: 'La missió ha fallat',
    } as Record<string, string>,
    endedOther: 'S’ha acabat la missió',
    clockExpired: 'S’ha acabat el temps de la missió',
    clockLeft: (minutes: number) =>
      minutes === 1 ? 'Queda 1 minut de missió' : `Queden ${minutes} minuts de missió`,
    ordersBy: (who: string) => `${who} ha recollit les ordres`,
    ordersByRadio: 'Les ordres han arribat per ràdio',
    ordersTaken: 'S’han rebut les ordres',
    vipLost: 'S’ha perdut la persona protegida',
    other: 'Novetat del servidor',
    minute: (m: number) => `min ${m}`,
    minutes: (from: number, to: number) => `min ${from}–${to}`,
    /** What a screen reader hears in place of the bare "44′". */
    open: 'Mostra-les una per una',
    close: 'Amaga-les',
  },
  /** When a session that has not started starts: the date tile, the countdown, and what stands in for them. */
  sessionWhen: {
    label: 'Compte enrere',
    days: (n: number) => (n === 1 ? 'dia' : 'dies'),
    hours: 'hores',
    minutes: 'minuts',
    seconds: 'segons',
    undatedTitle: 'Sense data ni hora',
    undatedHint:
      'La direcció l’arrenca quan toca. Mentrestant pots llegir el briefing i veure qui hi serà.',
  },
  /** The session page's three faces (the owner, 2026-10-12): before, live, over. */
  sessionPage: {
    /* A night the reader may not see answers like one that does not exist: the page does not say which. */
    unavailable: 'Aquesta sessió no està disponible.',
    unavailableHint: 'Pot ser que no existeixi o que no hi tinguis accés.',
    unavailableBack: 'Torna a les sessions',
    /* The way to the direction, for whoever directs: the session page never draws what it draws. */
    directionTitle: 'Dirigeixes aquesta partida',
    directionText:
      'Aquesta pàgina és la que veuen els jugadors, sense cap sorpresa. Tots els objectius, l’enemic i les ordres són a la teva pàgina de direcció.',
    directionOpen: 'Obre la direcció',
    /* Who was there, once it is over */
    attendeesHint: 'Les persones que hi han jugat, segons el servidor i el panell.',
    attendeeUnnamed: 'Jugador',
    /* The briefing's own objective section: what for, in general words, never where the enemy is */
    objectiveSection: 'Objectiu',
    objectiveLead: 'Què heu d’aconseguir',
    objectiveNone:
      'Encara no hi ha cap objectiu conegut: la direcció els donarà durant la partida.',
    objectiveByType: {
      clear_area: 'Netejar la zona d’enemics i mantenir-ne el control.',
      destroy_installation:
        'Destruir una instal·lació enemiga i marxar abans que arribi el reforç.',
      capture_hvt: 'Capturar viu un objectiu d’alt valor, sense deixar que s’escapi.',
      rescue_hostage: 'Trobar els ostatges i portar-los sans i estalvis fins a l’extracció.',
      defend_position: 'Mantenir la posició contra els atacs enemics fins que us rellevin.',
      escort_convoy: 'Acompanyar el comboi fins al seu destí sense perdre’l pel camí.',
      clear_route: 'Obrir la ruta de la columna: trobar i neutralitzar el que la talla.',
      retrieve_intel: 'Recuperar la informació que l’enemic guarda i endur-vos-la.',
      defuse_bomb: 'Desactivar l’artefacte abans que esclati i protegir la gent del voltant.',
      deliver_supplies: 'Fer arribar el material al lloc on l’esperen.',
      humanitarian_aid: 'Atendre la població de la zona i guanyar-ne la confiança.',
      ambush_convoy: 'Emboscar la columna enemiga quan passi per la ruta.',
      ambush_reaction: 'Respondre a la força que l’enemic envia en reacció.',
      sabotage_vehicles: 'Deixar els vehicles enemics fora de servei.',
      exfiltrate: 'Sortir de la zona tots junts pel punt d’extracció.',
      bribe_informant: 'Parlar amb l’informador i aconseguir el que sap.',
      observe_position: 'Observar una posició i informar-ne sense ser detectats.',
      find_cache: 'Localitzar l’amagatall de material de la zona.',
      secure_hlz: 'Assegurar una zona d’aterratge per al helicòpter.',
      mark_target: 'Marcar l’objectiu perquè el suport el pugui batre.',
      escort_vip: 'Acompanyar la persona fins al destí sense que li passi res.',
      evade_to: 'Anar-vos-en sense ser detectats fins al punt de sortida.',
    } as Record<string, string>,
    objectiveDefault: 'Complir l’ordre del comandament a la zona.',
    /** The three words each objective of the briefing is read by (web card 187). */
    objectiveAsks: 'Què demana',
    objectiveWhen: 'Quan comença',
    objectiveOnMap: 'Al mapa',
    /** When an objective starts: from the start, after the ones before it, or when it is given. */
    objectiveFromStart: 'Des del principi de la partida.',
    objectiveAfter: (titles: string[]) =>
      `Quan s’hagi complert ${titles.map((t) => `«${t}»`).join(' i ')}.`,
    objectiveAfterUnknown:
      'Quan s’hagi complert un altre objectiu, que coneixereu durant la partida.',
    objectiveGiven: 'Durant la partida, quan se us dona.',
    /** The circle the map draws round an objective: the objective is inside it, not at its middle. */
    objectiveCircle: (metres: number) =>
      `Un cercle d’uns ${metres} m de radi: l’objectiu és a dins, no necessàriament al mig.`,
    objectiveNoCircle: 'Només el seu número, sense cercle: l’objectiu és on és el número.',
    /** Under the order's own text: the page and the game read the same. */
    asInGame: 'Tal com ho llegireu al briefing del joc.',
    /* The map of the briefing: the area and where the night starts, nothing of the enemy */
    areaMapGeneral:
      'L’àrea marcada és on es juga la partida. L’enemic no és al mapa: l’heu de trobar.',
    signInAccount: 'Entra al compte',
    banner: 'La sessió',
    terrain: 'Terreny',
    slots: (n: number) => (n === 1 ? '1 plaça' : `${n} places`),
    when: 'Dia i hora',
    hourInGame: (h: string) => `${h} al joc`,
    countdown: 'Compte enrere',
    join: 'Uneix-te ara',
    /* Live */
    statusStrip: 'Estat de la partida',
    inPlay: 'En joc',
    minute: (m: number) => `minut ${m}`,
    players: (n: number) => (n === 1 ? '1 jugador' : `${n} jugadors`),
    radioLog: 'Ràdio i comandament',
    radioLogEmpty: 'Encara no s’ha dit res per ràdio.',
    /* Over */
    result: 'Resultat',
    resultUnknown: 'Sessió acabada',
    duration: (text: string) => `Durada ${text}`,
    figures: 'Xifres de la partida',
    /* The small map of an ended night, and the card for the next chapter */
    outcomeMapTitle: 'Objectius sobre el mapa',
    outcomeMapLabel: (world: string) => `Mapa de ${world} amb el resultat de cada objectiu`,
    outcomeLegend: { done: 'Fet', failed: 'Fallat', other: 'Sense resoldre' },
    nextChapterTitle: 'Capítol següent',
    nextChapterEmpty:
      'Encara no hi ha cap votació oberta per al capítol següent. Quan n’hi hagi una, la veuràs aquí.',
    nextChapterChosen: (title: string) => `El grup ha triat: ${title}.`,
    nextChapterOpen: 'Obre la campanya',
    objectivesDone: (done: number, total: number) =>
      total === 1 ? `${done} d’1 objectiu complert` : `${done} de ${total} objectius complerts`,
    objectivesLabel: 'Objectius complerts',
    ownCasualties: 'Baixes pròpies',
    enemyCasualties: 'Baixes enemigues',
    civilianCasualties: 'Baixes civils',
    playersLabel: 'Jugadors',
    durationLabel: 'Durada',
    whoWasThere: 'Qui hi era',
    whoWasThereHint: 'Segons l’última llista del servidor.',
    /* Tester and admin */
    quietLinks: 'Proves i dades tècniques',
    /* Briefing */
    situation: 'Situació',
    areaMap: 'Àrea d’operacions',
    areaMapHint: 'Els números del mapa són els dels objectius de sota.',
    howWeGetThere: 'Com hi arribem',
    way: (means: string) => `Inserció: ${means.toLowerCase()}`,
    wayOut: (means: string) => `Extracció: ${means.toLowerCase()}`,
    vehiclesAtStart: 'Vehicles que trobareu al punt de partida',
    vehicle: 'Vehicle',
    vehicleCount: (n: number) => `×${n}`,
    noVehicles: 'Cap vehicle al punt de partida.',
    noSupports: 'Aquesta partida no té cap suport: el que porteu és el que teniu.',
    /** The supports card of the session page, before and after the night. */
    supportsTitle: 'Suports de la partida',
    mods: 'Mods de la partida',
    backToSession: 'Torna a la sessió',
    /** The hour of the day as a clock reads it; the generator writes half hours too (22.5 is 22:30). */
    hour: (h: number) => {
      const minutes = Math.round((((h % 24) + 24) % 24) * 60) % 1440;
      const two = (n: number) => String(n).padStart(2, '0');
      return `${two(Math.floor(minutes / 60))}:${two(minutes % 60)}`;
    },
  },
  live: {
    now: 'Ara',
    commander: (name: string) => `Comandament: ${name}`,
    commanderNone: 'Cap comandant de la nit encara.',
    commanderTitle: 'Comandant de la nit',
    commanderHint:
      'Qui mana aquesta nit, només en aquesta sessió: obre i tanca les votacions i pot passar el comandament a un altre jugador. El tries tu o el vota el grup.',
    /** The two ways to name one, side by side so neither hides behind the other (web card 223). */
    commanderAppointTitle: 'El tries tu',
    commanderHandle: 'Nom o nom d’usuari',
    commanderAppoint: 'Nomena',
    commanderClear: 'Treu el comandant',
    commanderVote: 'Que el grup el voti',
    commanderVoteHint:
      'Afegeix els candidats d’un en un. La votació surt a «Votacions» i qui guanyi comandarà la nit.',
    commanderVoteCandidates: 'Afegeix un candidat',
    commanderVoteNone: 'Encara cap candidat: n’hi calen dos com a mínim.',
    commanderVoteRemove: (name: string) => `Treu ${name} dels candidats`,
    commanderVoteAdd: 'Afegeix',
    commanderVoteOpen: 'Obre la votació del comandant',
    commanderVoteQuestion: 'Qui comanda aquesta nit?',
    commanderVoteOpened: 'Votació oberta: qui guanyi comandarà la nit.',
    youCommand: 'Comandes aquesta nit',
    youCommandText:
      'En aquesta sessió obres i tanques les votacions. Pots passar el comandament a un altre jugador.',
    handOverTo: 'Nom d’usuari de qui el rep',
    /** Under the field that names an account, when nothing matches what was typed. */
    pickerNone: 'Cap compte coincideix amb el que has escrit.',
    handOver: 'Passa el comandament',
    handedOver: (name: string) => `Has passat el comandament a ${name}.`,
    quorumLabel: 'Tanca sola quan hagin votat (persones, buit = no)',
    voted: (n: number, quorum: number | null) =>
      quorum ? `${n} de ${quorum} han votat` : n === 1 ? '1 ha votat' : `${n} han votat`,
    noObjective: 'Cap objectiu pendent ara mateix.',
    updated: (s: number) => (s < 2 ? 'actualitzat ara mateix' : `actualitzat fa ${s} s`),
    signInToVote: 'Entra per votar',
    closesIn: (t: string) => `Tanca en ${t}`,
    closedVotes: 'Votacions tancades',
    supports: 'Suports',
    serverCard: 'Servidor',
    scheduledFor: (when: string) => `Sessió programada per ${when}`,
    undated: 'Encara sense hora fixada',
    when: 'Quan',
    serverNone: 'Cap servidor assignat encara: qui dirigeix el tria en arrencar la sessió.',
    serverAddress: 'Adreça',
    serverAddressUnknown: 'adreça no configurada',
    joinNow: 'Uneix-te ara',
    outcomeBanner: 'Resultat de la partida',
    /** The partial end's own sentence (the owner, 2026-09-27), under its chip. */
    partialLine: 'Missió parcialment complerta. Ens retirem amb el que tenim: tanquem l’operació.',
    startingHint:
      'El servidor està arrencant i la missió començarà sola en un parell de minuts. Llegeix el briefing mentrestant.',
    noServerTitle: 'Sense servidor',
    noServerHint:
      'La sessió es va arrencar sense cap servidor assignat, així que no hi ha res en marxa. Tria un servidor aquí sota i torna-la a arrencar.',
    /**
     * A start of the session that is under way (2026-10-26): the platform stops
     * the server on purpose and starts it again, and the page called that a
     * server that was down. The step it is on, in a person's words.
     */
    startingTitle: 'S’està engegant…',
    startingSteps: {
      preparing: 'Preparant la missió per al servidor.',
      candidate: 'Posant la versió de prova del mod al servidor.',
      checking: 'Mirant que el servidor es pugui tocar.',
      stopping: 'Aturant el servidor perquè carregui aquesta missió.',
      template: 'Posant la missió al servidor.',
      starting: 'Engegant el servidor.',
      waiting: 'Esperant que el servidor respongui. Pot trigar un parell de minuts.',
    } as Record<string, string>,
    startingWait: 'Ja s’està arrencant: espera que acabi abans de tornar-ho a demanar.',
    downTitle: 'El servidor no està en marxa',
    downHint:
      'El panell diu que el servidor de la sessió està aturat, així que ara mateix no s’hi pot entrar. Torna a arrencar la sessió quan vulguis.',
    awayTitle: (minutes: number) =>
      minutes < 2
        ? 'La plataforma ha estat fora un minut'
        : `La plataforma ha estat fora uns ${minutes} min`,
    awayHint: (from: string, to: string) =>
      `De ${from} a ${to} la plataforma no ha rebut res del servidor. La partida ha continuat i ja s’ha posat al dia.`,
    loopTitle: 'Servidor aturat: la missió no arrencava',
    loopHint:
      'El servidor reiniciava la missió en bucle i la plataforma l’ha aturat. Revisa la missió i torna a arrencar la sessió.',
    elapsedLabel: 'Temps de joc',
    startsIn: (t: string) => `Comença en ${t}`,
    startingNow: 'Comença ara mateix',
    scheduledTitle: 'Sessió preparada',
    /** "1:04:22" while it lasts more than an hour, "4:22" before that. */
    clock: (seconds: number) => {
      const s = Math.max(0, Math.floor(seconds));
      const two = (n: number) => String(n).padStart(2, '0');
      return s >= 3600
        ? `${Math.floor(s / 3600)}:${two(Math.floor((s % 3600) / 60))}:${two(s % 60)}`
        : `${Math.floor(s / 60)}:${two(s % 60)}`;
    },
    /** How long until the session starts, in the largest two units that say something. */
    until: (seconds: number) => {
      const s = Math.max(0, Math.floor(seconds));
      const days = Math.floor(s / 86400);
      const hours = Math.floor((s % 86400) / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      if (days > 0) return `${days} d ${hours} h`;
      if (hours > 0) return `${hours} h ${minutes} min`;
      if (minutes > 0) return `${minutes} min`;
      return `${s} s`;
    },
  },
  /** The live session page as designed (UI doc 5.1): header strip, NOW, DECIDE, SUPPORTS, the bar, the states. */
  sessionNow: {
    phase: (n: number, name: string) => `Fase ${n} · ${name}`,
    phaseOf: (n: number, total: number) =>
      `Fase ${n} de ${total}; les fases surten del graf d’objectius`,
    phaseNames: {
      insertion: 'Inserció',
      objectives: 'Objectius',
      extraction: 'Extracció',
    } as Record<string, string>,
    inGame: 'Hora al joc',
    inGameHint:
      'L’hora dins el joc segons l’última lectura del servidor, comptada des d’aleshores.',
    heartbeat: {
      live: 'Connectat',
      stale: 'Sense notícies recents',
      disconnected: 'Sense connexió',
      loading: 'Connectant',
    } as Record<string, string>,
    you: 'El teu perfil',
    grid: 'Quadrícula',
    gridHint:
      'Centenars de metres des de la cantonada sud-oest del mapa, com la quadrícula del mapa d’aquesta pàgina.',
    progress: (done: number, total: number) =>
      `${done} de ${total} ${total === 1 ? 'objectiu resolt' : 'objectius resolts'}`,
    radio: 'Última línia de ràdio',
    allResolved: 'Tots els objectius resolts: cap a l’extracció.',
    details: 'Detalls de l’objectiu',
    notYet: 'encara no disponible',
    distance: 'Distància',
    distanceWhy:
      'El servidor encara no ha dit on són els jugadors. La lectura arriba cada mig minut mentre hi ha algú al joc, i només es veu amb la sessió iniciada.',
    distanceValue: (metres: number) =>
      metres >= 1000 ? `${(metres / 1000).toFixed(1).replace('.', ',')} km` : `${metres} m`,
    distanceHint:
      'Fins a l’objectiu, des del jugador més proper segons l’última lectura del servidor.',
    holdTotal: (minutes: number) => `Cal mantenir la posició ${minutes} min`,
    holdLeft: 'Temps que queda',
    holdLeftWhy:
      'El compte enrere comença quan algú arriba a la zona; el servidor ho dirà aquí tot seguit.',
    holdNotStarted: 'encara no ha començat',
    holdPaused: 'aturat',
    holdPausedHint: 'El temps no corre: no hi ha ningú a la zona o hi ha l’enemic.',
    holdClock: (seconds: number) =>
      `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
    supportsHint: 'Es demanen des del joc, no des del telèfon.',
    noLimit: 'sense límit',
    noLimitHint: 'Un servei de Simplex porta la seva pròpia recàrrega, que el servidor no llegeix.',
    ready: 'disponible',
    coolingFor: (seconds: number) =>
      seconds >= 60 ? `en recàrrega, ${Math.ceil(seconds / 60)} min` : `en recàrrega, ${seconds} s`,
    fromDefinition:
      'Segons la missió. El servidor diu cada pocs minuts quin està en recàrrega; fins aleshores, tots es donen per disponibles.',
    fromSnapshot: (ago: string) => `Segons el servidor, ${ago}.`,
    commandDecision: 'Última decisió de comandament',
    noCommandDecision: 'Cap decisió de comandament encara.',
    supportGranted: (n: number, name: string) => `Comandament: +${n} ${name}`,
    supportReady: (name: string) => `Comandament: ${name} disponible ara`,
    objectiveAdded: (title: string) => `Comandament: objectiu nou, ${title}`,
    reinforcements: 'Comandament: reforços en camí',
    decide: 'Decideix',
    votedOf: (n: number, of: number) => `${n} de ${of} han votat`,
    voteChange: 'Toca una altra opció per canviar el vot mentre sigui oberta.',
    roster: 'Jugadors',
    rosterOnServer: (n: number) =>
      n === 1 ? '1 persona al servidor' : `${n} persones al servidor`,
    rosterAsOf: (ago: string) => `Llista del servidor ${ago}.`,
    rosterNone: 'El servidor encara no ha dit qui hi ha.',
    rosterConnected: (n: number) => (n === 1 ? '1 connectat ara' : `${n} connectats ara`),
    rosterSide: (side: string | null) =>
      ({
        WEST: 'BLUFOR',
        EAST: 'OPFOR',
        GUER: 'Independents',
        INDEPENDENT: 'Independents',
        RESISTANCE: 'Independents',
        CIV: 'Civils',
        CIVILIAN: 'Civils',
      })[(side ?? '').toUpperCase()] ?? null,
    rosterCounts: (alive: number, dead: number) =>
      `${alive} ${alive === 1 ? 'viu' : 'vius'} · ${dead} ${dead === 1 ? 'mort' : 'morts'}`,
    rosterRole: 'Rol',
    rosterRoleWhy: 'El servidor encara no diu quin rol porta cadascú.',
    rosterPositions: 'Posició',
    rosterPositionsWhy: 'El servidor encara no diu on és cada jugador.',
    alive: 'viu',
    dead: 'mort',
    ago: (seconds: number) => {
      const s = Math.max(0, Math.floor(seconds));
      if (s < 2) return 'ara mateix';
      if (s < 60) return `fa ${s} s`;
      if (s < 3600) return `fa ${Math.floor(s / 60)} min`;
      return `fa ${Math.floor(s / 3600)} h`;
    },
    loading: 'Carregant la sessió…',
    disconnected: 'Sense connexió amb la plataforma',
    disconnectedHint:
      'Es mostra l’últim estat conegut. La pàgina ho torna a provar sola cada deu segons.',
    offline: 'El dispositiu no té connexió',
    retry: 'Torna-ho a provar',
    barNow: 'Ara',
    barMap: 'Mapa',
    mapTitle: 'Mapa de la sessió',
    mapOpen: 'Mapa a pantalla completa',
    mapClose: 'Tanca el mapa',
    sheetClose: 'Tanca els detalls',
    sheetEmpty: 'Toca un objectiu del mapa per veure’n els detalls.',
    type: 'Tipus',
    state: 'Estat',
    actions: 'Accions de la sessió',
  },
  /** The terrain map's own controls; the map is shared by the designer, the mission, the session and the direction pages. */
  mapUi: {
    zoomIn: 'Apropa el mapa',
    zoomOut: 'Allunya el mapa',
    fit: 'Tot el mapa',
    fitArea: 'Enquadra l’àrea d’operacions',
    zoomLevel: (scale: number) => `×${scale.toFixed(1)}`,
    startPoint: 'Punt de partida',
    civilians: 'Zona civil',
    layers: 'Capes',
    grid: 'Quadrícula',
    atlasCredit: 'Mapes: PLANOPS Atlas',
  },
  /** How to get into the server: what the platform knows, and what the players have to type. */
  join: {
    title: 'Com s’hi entra',
    card: 'Aquesta és la prova',
    server: 'Servidor',
    address: 'Adreça',
    password: 'Contrasenya dels jugadors',
    noPassword: 'Sense contrasenya',
    addressUnknown: 'La plataforma encara no sap l’adreça d’aquest servidor.',
    addressUnknownHint:
      'L’adreça pública es configura al panell; mentrestant, demana-la a qui dirigeix.',
    copyAddress: 'Copia l’adreça',
    copyPassword: 'Copia la contrasenya',
    copied: 'Copiat',
    steamHint: 'El botó obre Arma 3 i s’hi connecta.',
  },
  /** The objectives card, shared by the session and the direction pages. */
  objectiveUi: {
    select: 'Mostra’l al mapa',
    edit: 'Edita l’objectiu',
    save: 'Desa l’objectiu',
    cancelEdit: 'Deixa-ho estar',
    none: 'Aquesta missió no té cap objectiu.',
    editHint: 'El canvi es desa a la missió; el que ja corre al servidor no es reescriu.',
    states: {
      pending: 'pendent',
      active: 'en curs',
      done: 'fet',
      failed: 'fallat',
      skipped: 'omès',
      cancelled: 'cancel·lat',
    } as Record<string, string>,
    types: {
      clear_area: 'Neteja la zona',
      destroy_installation: 'Destrueix la instal·lació',
      capture_hvt: 'Captura l’objectiu d’alt valor',
      rescue_hostage: 'Allibera els ostatges',
      defend_position: 'Defensa la posició',
      escort_convoy: 'Escorta el comboi',
      clear_route: 'Obre la ruta',
      retrieve_intel: 'Recupera informació',
      defuse_bomb: 'Desactiva la bomba',
      deliver_supplies: 'Entrega subministraments',
      humanitarian_aid: 'Ajuda humanitària',
      ambush_convoy: 'Embosca el comboi',
      ambush_reaction: 'Embosca la reacció',
      sabotage_vehicles: 'Sabota els vehicles',
      exfiltrate: 'Exfiltra’t',
      bribe_informant: 'Suborna l’informador',
    } as Record<string, string>,
  },
};
